import { useState } from 'react';
import type { AnimationEvent } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import style from './Input.module.scss';
import { InputElement, InputProps, InputWithErrorMessageProps } from './Input.types';

import { withErrorMessage } from './withErrorMessage';
import { useFieldFocus } from '../../hooks/useFieldFocus';

function stringValueMapper(v: any) {
  return `${v}`;
}

// eslint-disable-next-line prefer-arrow-callback
export const InputBaseV2 = <C extends InputElement = 'input'>(props: InputProps<C>) => {
  const {
    ref,
    as: Component = 'input',
    className,
    classNameLabel,
    classNameInput,
    disabled,
    validDisabled,
    errorMessage,
    hasError,
    helperMessage,
    id,
    label,
    success,
    successMessage,
    maxLength,
    placeholder,
    renderAddon,
    type = 'text',
    value: rawValue,
    valueMapper = stringValueMapper,
    focusedManually,
    onClick,
    render,
    onChange,
    onFocus,
    onBlur,
    dataAutofillMonitor,
    isPassword,
    errorMessageString,
    ...inputProps
  } = props;
  const { t } = useTranslation('checkout');

  const value = rawValue !== undefined && rawValue !== null ? valueMapper(rawValue) : undefined;
  const isDisabled = disabled || (validDisabled && !hasError);
  const { handleFocus, handleBlur, focused } = useFieldFocus(onFocus, onBlur, isDisabled);
  const [focusAutoFill, setFocusAutoFill] = useState(false);
  const onAnimationAutoFill = (e: AnimationEvent<HTMLInputElement>) => {
    if (!dataAutofillMonitor) {
      return;
    }
    if (e.animationName === style.autofillMonitorStart) {
      setFocusAutoFill(true);
    } else {
      setFocusAutoFill(false);
    }
  };

  const classNames = clsx(
    style.field,
    isDisabled && style.disabled,
    hasError && style.error,
    success && style.success,
    className
  );

  const inputClassNames = clsx(style.input, classNameInput, {
    [style.notEmpty]: value || placeholder,
    [style.withAddon]: renderAddon,
    [style.onlyInput]: !label
  });
  const labelClassNames = clsx(style.label, classNameLabel, {
    [style.notEmpty]: value || placeholder,
    [style.focused]: focused || focusedManually || focusAutoFill
  });
  const hintClassNames = isPassword ? clsx(style.passwordHint) : clsx(style.hint);
  const hintElement = hasError ? errorMessage : successMessage || helperMessage;

  return (
    <div className={classNames}>
      {render ? (
        render({
          onFocus: handleFocus,
          onBlur: handleBlur,
          classNames: inputClassNames
        })
      ) : (
        <>
          <Component
            {...inputProps}
            onChange={onChange}
            ref={ref}
            className={inputClassNames}
            id={id}
            maxLength={maxLength}
            placeholder={placeholder}
            value={value}
            disabled={isDisabled}
            type={type}
            onClick={onClick}
            onFocus={onFocus}
            onBlur={onBlur}
            data-autofill-monitor={dataAutofillMonitor}
            onAnimationStart={(e) => onAnimationAutoFill(e)}
          />
          {renderAddon?.({
            className: style.addon,
            disabled: isDisabled
          })}
        </>
      )}
      {label && (
        <label htmlFor={id} className={labelClassNames}>
          {label}
        </label>
      )}
      {hintElement && (
        <strong className={hintClassNames}>
          {t(`thanksFields.${errorMessageString}`, '') || hintElement}
        </strong>
      )}
    </div>
  );
};

export const InputV2 = withErrorMessage<HTMLInputElement, InputWithErrorMessageProps>(InputBaseV2);
